/**
 * Utility functions for storing and retrieving API endpoints with timestamps
 */

const ENDPOINTS_KEY = 'service_endpoints';
const ENDPOINTS_TIMESTAMP_KEY = 'api_endpoints_timestamp';
const MAX_AGE_MS = 3 * 60 * 60 * 1000; // 3 hours in milliseconds

// Flag to track if clearing is in progress
let clearingInProgress = false;

/**
 * Store endpoints in localStorage with current timestamp
 * @param {Object} endpoints - The endpoints data to store
 */
export const storeEndpoints = (endpoints) => {
  if (!endpoints) {
    console.error('No endpoints provided to store');
    return;
  }
  
  // Add timestamp if not present
  if (!endpoints.fetchedAt) {
    endpoints.fetchedAt = new Date().toISOString();
  }
  
  try {
    // Log the structure being stored
    console.log('Storing endpoints with structure:', {
      type: typeof endpoints,
      isArray: Array.isArray(endpoints),
      keys: typeof endpoints === 'object' ? Object.keys(endpoints) : 'not an object',
      preview: JSON.stringify(endpoints).substring(0, 100) + '...'
    });
    
    // Store the endpoints data
    localStorage.setItem(ENDPOINTS_KEY, JSON.stringify(endpoints));
    
    // Store the current timestamp
    const timestamp = Date.now();
    localStorage.setItem(ENDPOINTS_TIMESTAMP_KEY, timestamp.toString());
    
    console.log('Endpoints stored with timestamp:', new Date(timestamp).toISOString());
  } catch (error) {
    console.error('Error storing endpoints:', error);
    
    if (error instanceof TypeError) {
      console.error('Type error. Endpoints might contain circular references or non-serializable values.');
    }
  }
};

/**
 * Get stored endpoints from localStorage
 * @returns {Object|null} The stored endpoints or null if not found
 */
export const getStoredEndpoints = () => {
  const storedData = localStorage.getItem(ENDPOINTS_KEY);
  if (!storedData) return null;
  
  try {
    const endpoints = JSON.parse(storedData);
    
    // Log retrieved structure
    console.log('Retrieved endpoints with structure:', {
      type: typeof endpoints,
      isArray: Array.isArray(endpoints),
      keys: typeof endpoints === 'object' ? Object.keys(endpoints) : 'not an object',
      timestamp: localStorage.getItem(ENDPOINTS_TIMESTAMP_KEY)
        ? new Date(parseInt(localStorage.getItem(ENDPOINTS_TIMESTAMP_KEY), 10)).toISOString()
        : 'no timestamp'
    });
    
    return endpoints;
  } catch (error) {
    console.error('Error retrieving stored endpoints:', error);
    
    if (error instanceof SyntaxError) {
      console.error('Invalid JSON in stored endpoints. Clearing corrupt data.');
      clearEndpoints();
    }
    
    return null;
  }
};

/**
 * Check if stored endpoints exist and are fresh (less than MAX_AGE_MS old)
 * @returns {boolean} True if endpoints exist and are fresh
 */
export const hasValidEndpoints = () => {
  const endpoints = getStoredEndpoints();
  if (!endpoints) return false;
  
  // Check if endpoints have a fetchedAt timestamp
  if (!endpoints.fetchedAt) return false;
  
  // Check if endpoints are still fresh (less than max age)
  const fetchedAt = new Date(endpoints.fetchedAt).getTime();
  const now = Date.now();
  const age = now - fetchedAt;
  
  return age < MAX_AGE_MS;
};

/**
 * Clear stored endpoints and timestamp
 */
export const clearEndpoints = () => {
  // Prevent duplicate clearing operations
  if (clearingInProgress) {
    console.log('Endpoint clearing already in progress, skipping duplicate call');
    return;
  }
  
  clearingInProgress = true;
  
  try {
    console.log('Clearing stored endpoints and timestamp');
    localStorage.removeItem(ENDPOINTS_KEY);
    localStorage.removeItem(ENDPOINTS_TIMESTAMP_KEY);
  } finally {
    // Reset flag after a short delay
    setTimeout(() => {
      clearingInProgress = false;
    }, 500);
  }
};

/**
 * Get the WebSocket URL from storage with proper protocol handling and API path
 * @returns {string} The WebSocket URL with API path or null if not found
 */
export const getWebSocketUrl = () => {
  const endpoints = getStoredEndpoints();
  if (!endpoints || !endpoints.websocket) return null;
  
  let url = endpoints.websocket;
  
  // Ensure proper WebSocket protocol
  if (url.startsWith('ws://') || url.startsWith('wss://')) {
    // Protocol already correct
  } else if (url.startsWith('http://')) {
    url = url.replace('http://', 'ws://');
  } else if (url.startsWith('https://')) {
    url = url.replace('https://', 'wss://');
  } else if (!url.includes('://')) {
    url = `wss://${url}`;
  } else {
    console.warn('Unusual URL format for WebSocket:', url);
  }
  
  // Ensure the API path suffix is present
  if (!url.endsWith('/api/v1') && !url.includes('/api/v1/')) {
    // Remove trailing slash if present before adding suffix
    if (url.endsWith('/')) {
      url = url.slice(0, -1);
    }
    url += '/api/v1';
    console.log(`Added API path suffix to WebSocket URL: ${url}`);
  }
  
  return url;
}; 